import React from 'react';
import {
  ButtonProps,
  HiveTooltipProps,
} from '@routific/hive';
import {
  Button,
  colorVars,
  MenuRow,
  Tooltip,
  Typography,
} from '@routific/hive/v5';
import type { V5ButtonHierarchy } from '@routific/hive/v5';
import { MenuItemProps } from 'antd';

interface Props {
  type: 'button' | 'menu' | 'textButton';
  tooltipProps?: HiveTooltipProps;
  disabled?: boolean;
  loading?: boolean;
  menuItemProps?: MenuItemProps;
  menuItemText?: string;
  buttonProps: ButtonProps;
  textButtonProps?: ButtonProps;
  icon?: React.ReactNode;
  dataCy: string;
  hierarchy?: V5ButtonHierarchy;
}

const ActionButtonV5: React.FC<Props> = ({
  type,
  tooltipProps,
  disabled,
  loading,
  menuItemProps,
  menuItemText,
  buttonProps,
  textButtonProps,
  icon,
  dataCy,
  hierarchy = 'tertiary',
}) => {
  if (type === 'menu') {
    const handleSelect = menuItemProps?.onClick as unknown as (() => void) | undefined;
    // A disabled menu row can't be hovered, so its tooltip would never show; say why inline.
    const disabledReason = disabled && tooltipProps?.title && tooltipProps.title !== menuItemText
      ? tooltipProps.title as React.ReactNode
      : undefined;

    return (
      <MenuRow
        icon={icon}
        destructive={buttonProps.danger}
        disabled={disabled || loading}
        onClick={() => handleSelect?.()}
        data-cy={dataCy}
        data-testid={dataCy}
      >
        {disabledReason ? (
          <span style={{ display: 'flex', flexDirection: 'column' }}>
            {menuItemText}
            <Typography variant="caption" style={{ color: colorVars.text.secondary.default, whiteSpace: 'normal' }}>
              {disabledReason}
            </Typography>
          </span>
        ) : menuItemText}
      </MenuRow>
    );
  }

  const isTextButton = type === 'textButton';
  const label = textButtonProps?.title ?? menuItemText;
  const onClick = (isTextButton ? textButtonProps?.onClick : buttonProps.onClick) as
    React.MouseEventHandler<HTMLButtonElement> | undefined;

  const button = (
    <Button
      hierarchy={hierarchy}
      tone={buttonProps.danger ? 'danger' : 'accent'}
      buttonType={isTextButton ? 'text-only' : 'icon-only'}
      size="md"
      icon={isTextButton ? undefined : icon}
      disabled={disabled}
      loading={loading}
      onClick={onClick}
      aria-label={isTextButton ? undefined : menuItemText}
      data-cy={dataCy}
      data-testid={dataCy}
    >
      {isTextButton ? label : undefined}
    </Button>
  );

  return tooltipProps?.title ? (
    <Tooltip title={tooltipProps.title as React.ReactNode}>
      {button}
    </Tooltip>
  ) : button;
};

export default ActionButtonV5;
